import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { legacyApi, GRN } from '../../../services/legacyApi';
import { CompactTable } from '../../../components/ui/CompactTable';
import { Button } from '../../../components/ui/button';
import { Plus, Eye, Pencil, Trash2, FilePlus2, X } from 'lucide-react';
import { useConfirm } from '../../../hooks/use-confirm';
import { toast } from 'sonner';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { FlowEntryBanner } from '../../../components/workflow/FlowEntryBanner';
import { useAuth } from '../../../context/AuthContext';
import { getGrnStatusMeta } from '../utils/statuses';

export const LegacyGRN: React.FC = () => {
    const PAGE_SIZE = 50;
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const { confirm } = useConfirm();
    const [grns, setGrns] = useState<GRN[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [debouncedQ, setDebouncedQ] = useState('');
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({
        q: '',
        source: 'all',
        bill_status: 'all'
    });

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedQ(state.q.trim()), 250);
        return () => window.clearTimeout(t);
    }, [state.q]);

    const loadGrns = async (reset: boolean = true) => {
        const nextOffset = reset ? 0 : grns.length;
        if (reset) setLoading(true);
        else setLoadingMore(true);
        try {
            const response = await legacyApi.purchasing.listGRNsPaged({
                q: debouncedQ || undefined,
                source: state.source !== 'all' ? state.source : undefined,
                bill_status: state.bill_status !== 'all' ? state.bill_status : undefined,
                limit: PAGE_SIZE,
                offset: nextOffset,
            });
            setGrns((prev) => (reset ? response.items : [...prev, ...response.items]));
            setTotalCount(response.total);
            setHasMore(response.has_more);
        } catch (err) {
            console.error(err);
        } finally {
            if (reset) {
                setLoading(false);
                setHasLoadedOnce(true);
            }
            else setLoadingMore(false);
        }
    };

    useEffect(() => {
        loadGrns(true);
    }, [debouncedQ, state.source, state.bill_status]);

    if (!hasLoadedOnce && loading) return <div className="p-4">Loading Goods Received Notes...</div>;

    const canCreateBill = hasPermission('purchasing.bill.create');
    const canEditGrn = hasPermission('purchasing.grn.edit');
    const canCancelGrn = hasPermission('purchasing.grn.cancel');
    const canDeleteGrn = hasPermission('purchasing.grn.delete');
    const canCreateBillForGrn = (g: GRN) => Boolean(g.can_create_bill);
    const canEditThisGrn = (g: GRN) => Number(g.status_id || 1) !== 4 && !((Boolean(g.linked_bill_id || g.auto_bill_id)) && Number(g.bill_status_id || 0) !== 4);
    const grnLockedByBillMessage = 'GRN is locked because a supplier bill exists. Cancel the linked supplier bill first, then edit the GRN.';
    const editReason = (g: GRN) => {
        if (Number(g.status_id || 1) === 4) return 'Cancelled GRN cannot be edited';
        if ((Boolean(g.linked_bill_id || g.auto_bill_id)) && Number(g.bill_status_id || 0) !== 4) return grnLockedByBillMessage;
        return 'Edit';
    };

    const sourceLabel = (value?: string) => {
        if (!value) return 'PO GRN';
        const token = String(value).toUpperCase();
        if (token === 'DIRECT_GRN') return 'Direct GRN';
        if (token === 'PO_GRN') return 'PO GRN';
        if (token === 'LEGACY') return 'Legacy';
        return value;
    };

    const billStatusMeta = (g: GRN): { key: string; label: string; className: string } => {
        if (!g.linked_bill_id && !g.auto_bill_id) {
            return { key: 'unbilled', label: 'Unbilled', className: 'bg-slate-100 text-slate-700 border-slate-200' };
        }
        const statusId = Number(g.bill_status_id || 1);
        if (statusId === 2) {
            return { key: 'posted', label: 'Posted', className: 'bg-blue-100 text-blue-700 border-blue-200' };
        }
        if (statusId === 3) {
            return { key: 'paid', label: 'Paid', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
        }
        if (statusId === 4) {
            return { key: 'cancelled', label: 'Cancelled', className: 'bg-red-100 text-red-700 border-red-200' };
        }
        if (statusId === 5) {
            return { key: 'partially_paid', label: 'Partially Paid', className: 'bg-amber-100 text-amber-700 border-amber-200' };
        }
        return { key: 'pending', label: 'Pending', className: 'bg-slate-100 text-slate-700 border-slate-200' };
    };

    const columns = [
        { header: "GRN ID", accessor: (g: GRN) => g.grn_number || `HC/GRN/${String(g.grn_id).padStart(4, '0')}`, className: 'font-mono whitespace-nowrap w-32' },
        {
            header: "PO No",
            accessor: (g: GRN) => (String(g.source_path || '').toUpperCase() === 'DIRECT_GRN' ? 'Direct (No PO)' : (g.po_number || `HC/PO/${String(g.po_id).padStart(4, '0')}`)),
            className: 'font-mono whitespace-nowrap w-32'
        },
        { header: "Supplier", accessor: (g: GRN) => g.supplier_name || '-', className: 'min-w-[180px] max-w-[240px] truncate' },
        { header: "Date Received", accessor: (g: GRN) => g.date_received ? new Date(g.date_received).toLocaleDateString() : '-', className: 'w-24 whitespace-nowrap' },
        { header: "Received By", accessor: (g: GRN) => g.received_by || '-', className: 'w-48 truncate' },
        {
            header: "GRN Status",
            accessor: (g: GRN) => {
                const meta = getGrnStatusMeta(g.status_id, g.status_name);
                return (
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${meta.badgeClass}`}>
                        {meta.label}
                    </span>
                );
            },
            className: 'w-28 whitespace-nowrap'
        },
        {
            header: "Source",
            accessor: (g: GRN) => sourceLabel(g.source_path),
            className: 'w-28 whitespace-nowrap'
        },
        {
            header: "Bill",
            accessor: (g: GRN) => (g.linked_bill_id || g.auto_bill_id ? `HC/BILL/${String(g.linked_bill_id || g.auto_bill_id).padStart(4, '0')}` : '-'),
            className: 'font-mono whitespace-nowrap w-32'
        },
        {
            header: "Bill Status",
            accessor: (g: GRN) => {
                const meta = billStatusMeta(g);
                return (
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${meta.className}`}>
                        {meta.label}
                    </span>
                );
            },
            className: 'w-32 whitespace-nowrap'
        },
        { header: "Delivery Note", accessor: (g: GRN) => g.delivery_note_no || '-', className: 'min-w-[150px] max-w-[200px] truncate' },
        {
            header: "Actions",
            accessor: (g: GRN) => (
                <div className="flex gap-1 justify-end">
                    {canCreateBill && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={!canCreateBillForGrn(g)}
                            onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                    const bill = await legacyApi.purchasing.createBill({
                                        grn_id: g.grn_id,
                                        bill_date: new Date().toISOString().split('T')[0],
                                        reference_no: g.delivery_note_no || undefined,
                                    });
                                    toast.success(`Supplier bill #${bill.bill_id} created.`);
                                    navigate(`/purchasing/bills/${bill.bill_id}`);
                                } catch (err: any) {
                                    toast.error(err?.message || 'Failed to create supplier bill');
                                }
                            }}
                            title={g.create_bill_reason || 'Create Supplier Bill'}
                        >
                            <FilePlus2 className={`w-4 h-4 ${canCreateBillForGrn(g) ? 'text-indigo-600' : 'text-muted-foreground'}`} />
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={!canEditGrn || !canEditThisGrn(g)}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/legacy/grn/edit/${g.grn_id}`);
                        }}
                        title={!canEditGrn ? 'No permission to edit GRN' : editReason(g)}
                        >
                        <Pencil className={`w-4 h-4 ${(!canEditGrn || !canEditThisGrn(g)) ? 'text-muted-foreground' : 'text-blue-600'}`} />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/legacy/grn/${g.grn_id}`);
                        }}
                        title="View Details"
                    >
                        <Eye className="w-4 h-4 text-green-600" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={!canCancelGrn || !Boolean(g.can_cancel)}
                        onClick={async (e) => {
                            e.stopPropagation();
                            const ok = await confirm('Cancel this GRN and reverse its stock/AP accrual impact?', { variant: 'destructive', confirmText: 'Cancel GRN', cancelText: 'Keep GRN' });
                            if (!ok) return;
                            try {
                                await legacyApi.purchasing.transitionGRN(g.grn_id, { action: 'cancel' });
                                toast.success('GRN cancelled');
                                await loadGrns(true);
                            } catch (err: any) {
                                console.error(err);
                                toast.error(err?.message || 'Failed to cancel GRN');
                            }
                        }}
                        title={!canCancelGrn ? 'No permission to cancel GRN' : (g.cancel_reason || 'Cancel GRN')}
                    >
                        <X className={`w-4 h-4 ${(!canCancelGrn || !Boolean(g.can_cancel)) ? 'text-muted-foreground' : 'text-amber-600'}`} />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={!canDeleteGrn || !Boolean(g.can_delete)}
                        onClick={async (e) => {
                            e.stopPropagation();
                            const ok = await confirm('Delete this GRN permanently?', { variant: 'destructive', confirmText: 'Delete', cancelText: 'Cancel' });
                            if (!ok) return;
                            try {
                                await legacyApi.purchasing.deleteGRN(g.grn_id);
                                setGrns(prev => prev.filter(x => x.grn_id !== g.grn_id));
                                setTotalCount(prev => Math.max(prev - 1, 0));
                                toast.success('GRN deleted');
                            } catch (err: any) {
                                console.error(err);
                                toast.error(err?.message || 'Failed to delete GRN');
                            }
                        }}
                        title={!canDeleteGrn ? 'No permission to delete GRN' : (g.delete_reason || 'Delete')}
                    >
                        <Trash2 className={`w-4 h-4 ${(!canDeleteGrn || !Boolean(g.can_delete)) ? 'text-muted-foreground' : 'text-red-600'}`} />
                    </Button>
                </div>
            ),
            className: "text-right"
        }
    ];

    return (
        <div className="h-full flex flex-col space-y-6 w-full">
            <div className="flex items-center justify-between space-y-2">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight text-foreground">Goods Received Notes</h2>
                    <p className="text-muted-foreground">Track receipt of goods against POs</p>
                </div>
                <Button asChild>
                    <Link to="/legacy/grn/new">
                        <Plus className="mr-2 h-4 w-4" />
                        New GRN
                    </Link>
                </Button>
            </div>
            <FlowEntryBanner
                title="GRN Flow Impact"
                pathLabel="PO to Supplier Bill"
                nextHint="GRN updates stock valuation and PO receiving progress before supplier billing."
                helpModule="purchasing"
            />
            <TableFilterBar
                searchValue={state.q}
                onSearchChange={(value) => setFilter('q', value)}
                searchPlaceholder="Search GRN, PO, supplier..."
                controls={[
                    {
                        key: 'source',
                        label: 'Source',
                        type: 'select',
                        value: state.source,
                        onChange: (value) => setFilter('source', value),
                        options: [
                            { value: 'all', label: 'All Sources' },
                            { value: 'po_grn', label: 'PO GRN' },
                            { value: 'direct_grn', label: 'Direct GRN' },
                            { value: 'legacy', label: 'Legacy' }
                        ]
                    },
                    {
                        key: 'bill_status',
                        label: 'Bill Status',
                        type: 'select',
                        value: state.bill_status,
                        onChange: (value) => setFilter('bill_status', value),
                        options: [
                            { value: 'all', label: 'All Bill Statuses' },
                            { value: 'unbilled', label: 'Unbilled' },
                            { value: 'pending', label: 'Pending' },
                            { value: 'posted', label: 'Posted' },
                            { value: 'partially_paid', label: 'Partially Paid' },
                            { value: 'paid', label: 'Paid' },
                            { value: 'cancelled', label: 'Cancelled' }
                        ]
                    }
                ]}
                isDirty={isDirty}
                onClear={clearAll}
                rightSlot={`${grns.length} of ${totalCount}`}
            />
            <div className="flex-1 overflow-auto rounded-md border">
                <>
                    <CompactTable
                        data={grns}
                        columns={columns}
                        keyField="grn_id"
                        loading={loading}
                        onRowClick={(row) => navigate(`/legacy/grn/${row.grn_id}`)}
                    />
                    {hasMore && (
                        <div className="flex justify-center mt-4">
                            <Button variant="ghost" onClick={() => loadGrns(false)} disabled={loadingMore}>
                                {loadingMore ? 'Loading...' : 'Load older GRNs'}
                            </Button>
                        </div>
                    )}
                </>
            </div>
        </div>
    );
};
